import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, Check, ChevronRight, Clock3, Grid2X2, Layers3, Plus, Rows3, Sparkles } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import API from "../services/api";

const modes = [
  { key: "single", label: "Single", type: "single", icon: "1", color: "#10b981", tint: "#eafaf3" },
  { key: "single_bulk", label: "Single Bulk", type: "single_bulk", icon: Grid2X2, color: "#14b8a6", tint: "#eafaf8" },
  { key: "sp", label: "Single Panna", type: "sp", icon: "123", color: "#8b5cf6", tint: "#f3efff" },
  { key: "dp", label: "Double Panna", type: "dp", icon: Layers3, color: "#f97316", tint: "#fff4ec" },
  { key: "tp", label: "Triple Panna", type: "tp", icon: Rows3, color: "#64748b", tint: "#f3f6f9" },
];

function StarlinePlayPage() {
  const { id, mode: modeParam } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const [markets, setMarkets] = useState([]);
  const [slots, setSlots] = useState([]);
  const [marketId, setMarketId] = useState(id || "");
  const [timeSlot, setTimeSlot] = useState(searchParams.get("slot") || "");
  const [mode, setMode] = useState("");
  const [digitAmounts, setDigitAmounts] = useState(Array(10).fill(""));
  const [bulkDigits, setBulkDigits] = useState([]);
  const [bulkAmount, setBulkAmount] = useState("");
  const [panna, setPanna] = useState("");
  const [pannaAmount, setPannaAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [ankStatus, setAnkStatus] = useState({ ank_limit: null, used_digits: [], used_count: 0, remaining: null });
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const requestedSlot = searchParams.get("slot") || "";
  const requestedMode = String(modeParam || "").trim();
  const isMobile = viewportWidth <= 480;
  const isTight = viewportWidth <= 390;
  const isActiveBetScreen = Boolean(mode);
  const busyRef = useRef(false);

  useEffect(() => {
    if (!user?.id) navigate("/login", { replace: true });
  }, [navigate, user]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!requestedMode) {
      setMode("");
      return;
    }
    const validMode = modes.some((item) => item.key === requestedMode) ? requestedMode : "";
    setMode(validMode);
  }, [requestedMode]);

  useEffect(() => {
    let mounted = true;
    API.get("/starline/control")
      .then((res) => {
        if (!mounted) return;
        const marketRows = Array.isArray(res.data?.markets) ? res.data.markets : [];
        const slotRows = Array.isArray(res.data?.slots)
          ? res.data.slots.filter((slot) => Number(slot.is_active) === 1)
          : [];
        const selectedMarketId = String(id || marketRows[0]?.id || "");
        const marketSlots = slotRows.filter((slot) => String(slot.market_id) === selectedMarketId);
        const resolvedSlot = marketSlots.find((slot) => slot.time_slot === requestedSlot)?.time_slot || marketSlots[0]?.time_slot || "";

        setMarkets(marketRows);
        setSlots(slotRows);
        setMarketId(selectedMarketId);
        setTimeSlot(resolvedSlot);
        if (resolvedSlot && resolvedSlot !== requestedSlot) {
          const nextParams = new URLSearchParams();
          nextParams.set("slot", resolvedSlot);
          setSearchParams(nextParams, { replace: true });
        }
      })
      .catch(() => {
        if (!mounted) return;
        setMarkets([]);
        setSlots([]);
      });
    return () => {
      mounted = false;
    };
  }, [id, requestedSlot, setSearchParams]);

  const marketSlots = slots.filter((slot) => String(slot.market_id) === String(marketId));
  const market = markets.find((item) => String(item.id) === String(marketId)) || markets[0];
  const activeMode = modes.find((item) => item.key === mode) || modes[0];

  useEffect(() => {
    if (!user?.id || !marketId || !timeSlot) return;
    API.get(`/users/${user.id}/ank-status?market_id=${encodeURIComponent(marketId)}&session=OPEN&time_slot=${encodeURIComponent(timeSlot)}`)
      .then((res) => setAnkStatus(res.data || { ank_limit: null, used_digits: [], used_count: 0, remaining: null }))
      .catch(() => setAnkStatus({ ank_limit: null, used_digits: [], used_count: 0, remaining: null }));
  }, [marketId, timeSlot, user]);

  const renderModeIcon = (item) => {
    const Icon = item.icon;
    return typeof Icon === "string" ? <span style={modeIconText}>{Icon}</span> : <Icon size={20} />;
  };

  const totalAmount = useMemo(() => {
    if (!mode) return 0;
    if (mode === "single") return digitAmounts.reduce((sum, item) => sum + Number(item || 0), 0);
    if (mode === "single_bulk") return bulkDigits.length * Number(bulkAmount || 0);
    return Number(pannaAmount || 0);
  }, [bulkAmount, bulkDigits.length, digitAmounts, mode, pannaAmount]);

  const resetInputs = () => {
    setDigitAmounts(Array(10).fill(""));
    setBulkDigits([]);
    setBulkAmount("");
    setPanna("");
    setPannaAmount("");
  };
  const usedDigitSet = useMemo(() => new Set((ankStatus.used_digits || []).map((value) => String(value))), [ankStatus.used_digits]);
  const getSelectedNewDigitCount = (nextDigitAmounts = digitAmounts, nextBulkDigits = bulkDigits) => {
    const selected = new Set();
    nextDigitAmounts.forEach((amount, digit) => {
      if (Number(amount || 0) > 0) selected.add(String(digit));
    });
    nextBulkDigits.forEach((digit) => selected.add(String(digit)));
    return Array.from(selected).filter((digit) => !usedDigitSet.has(digit)).length;
  };
  const exceedsAnkLimit = (nextDigitAmounts = digitAmounts, nextBulkDigits = bulkDigits) => {
    const limit = Number(ankStatus.ank_limit || 0);
    const remaining = Number(ankStatus.remaining || 0);
    return limit > 0 && getSelectedNewDigitCount(nextDigitAmounts, nextBulkDigits) > remaining;
  };
  const isNewDigitLocked = (digit, nextDigitAmounts = digitAmounts, nextBulkDigits = bulkDigits) => {
    const key = String(digit);
    if (usedDigitSet.has(key)) return false;
    if (Number(nextDigitAmounts[digit] || 0) > 0 || nextBulkDigits.includes(digit)) return false;
    const limit = Number(ankStatus.ank_limit || 0);
    const remaining = Number(ankStatus.remaining || 0);
    return limit > 0 && getSelectedNewDigitCount(nextDigitAmounts, nextBulkDigits) >= remaining;
  };

  const navigateWithSelection = (nextSlot, nextMode) => {
    const nextParams = new URLSearchParams();
    if (nextSlot) nextParams.set("slot", nextSlot);
    const nextPath = nextMode
      ? `/starline/${marketId || id || ""}/play/${nextMode}`
      : `/starline/${marketId || id || ""}/play`;
    navigate(`${nextPath}${nextParams.toString() ? `?${nextParams.toString()}` : ""}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const updateSingleDigitAmount = (digit, value) => {
    const digitKey = String(digit);
    const cleanValue = String(value || "").replace(/[^\d]/g, "");
    const nextAmounts = digitAmounts.map((item, index) => (index === digit ? cleanValue : item));
    if (Number(cleanValue || 0) > 0 && !usedDigitSet.has(digitKey) && exceedsAnkLimit(nextAmounts, bulkDigits)) {
      alert(`Ank limit reached for ${timeSlot}. Remove another ank before adding ${digitKey}.`);
      return;
    }
    setDigitAmounts(nextAmounts);
  };

  const toggleBulkDigit = (digit) => {
    const digitKey = String(digit);
    const alreadySelected = bulkDigits.includes(digit);
    const nextBulkDigits = alreadySelected ? bulkDigits.filter((item) => item !== digit) : [...bulkDigits, digit];
    if (!alreadySelected && !usedDigitSet.has(digitKey) && exceedsAnkLimit(digitAmounts, nextBulkDigits)) {
      alert(`Ank limit reached for ${timeSlot}. Remove another ank before adding ${digitKey}.`);
      return;
    }
    setBulkDigits(nextBulkDigits);
  };

  const makeBet = (number, amount, type) => ({
    user_id: user.id,
    market_id: marketId,
    number,
    amount,
    type,
    time_slot: timeSlot,
  });

  const submit = async () => {
    if (busyRef.current || busy) return;
    if (!user?.id || !marketId || !timeSlot || !mode) return;
    const payload = [];

    if (mode === "single") {
      digitAmounts.forEach((amount, digit) => {
        if (Number(amount || 0) > 0) payload.push(makeBet(String(digit), Number(amount), "single"));
      });
    } else if (mode === "single_bulk") {
      if (!bulkDigits.length || Number(bulkAmount || 0) <= 0) {
        alert("Select digits and enter amount");
        return;
      }
      bulkDigits.forEach((digit) => payload.push(makeBet(String(digit), Number(bulkAmount), "single_bulk")));
    } else if (panna && Number(pannaAmount || 0) > 0) {
      payload.push(makeBet(panna, Number(pannaAmount), activeMode.type));
    }

    if (!payload.length) return;
    if (["single", "single_bulk"].includes(mode) && exceedsAnkLimit()) {
      alert(`Ank limit is ${ankStatus.ank_limit} for ${timeSlot}. Remove extra ank before submit.`);
      return;
    }
    const uniquePayload = [];
    const seenBets = new Set();
    for (const item of payload) {
      const key = `${item.market_id}|${item.number}|${item.type}|${item.time_slot || ""}`;
      if (seenBets.has(key)) continue;
      seenBets.add(key);
      uniquePayload.push(item);
    }

    busyRef.current = true;
    setBusy(true);
    try {
      for (const item of uniquePayload) {
        await API.post("/place-bet", item);
      }
      resetInputs();
      alert("Starline bid placed");
      navigate(`/starline/${marketId}`);
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to place starline bid");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const updateMarket = (nextMarketId) => {
    setMarketId(nextMarketId);
    const nextSlot = slots.find((slot) => String(slot.market_id) === String(nextMarketId))?.time_slot || "";
    setTimeSlot(nextSlot);
    navigateWithSelection(nextSlot, "");
    resetInputs();
  };

  const updateSlot = (nextSlot) => {
    setTimeSlot(nextSlot);
    navigateWithSelection(nextSlot, mode);
  };

  return (
    <div style={{ ...page, ...(isMobile ? pageMobile : null), ...(isTight ? pageTight : null), ...(isActiveBetScreen && isMobile ? activePageMobile : null) }}>
      <header style={{ ...header, ...(isMobile ? headerMobile : null), ...(isActiveBetScreen && isMobile ? activeHeaderMobile : null) }}>
        <button
          type="button"
          style={backButton}
          onClick={() => {
            if (mode) {
              navigateWithSelection(timeSlot, "");
              resetInputs();
              return;
            }
            navigate(`/starline/${marketId || id || ""}`, { replace: true });
          }}
        >
          <ArrowLeft size={26} />
        </button>
        <h1 style={title}>
          <Sparkles size={20} color="#f59e0b" fill="#f59e0b" />
          {mode ? activeMode.label : "Starline Play"}
        </h1>
        <span style={headerSpacer} />
      </header>

      <main style={{ ...main, ...(isMobile ? mainMobile : null), ...(isTight ? mainTight : null), ...(isActiveBetScreen && isMobile ? activeMainMobile : null) }}>
        <section style={{ ...selectorCard, ...(isMobile ? selectorCardMobile : null), ...(mode && isMobile ? selectorCardActiveMobile : null), ...(isTight ? selectorCardTight : null) }}>
          <div>
            <label style={label}>Market *</label>
            <select style={{ ...select, ...(isMobile ? selectMobile : null), ...(isTight ? selectTight : null) }} value={marketId} onChange={(e) => updateMarket(e.target.value)}>
              {markets.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </div>
          <div>
            <label style={label}>Time Slot *</label>
            <select style={{ ...select, ...(isMobile ? selectMobile : null), ...(isTight ? selectTight : null) }} value={timeSlot} onChange={(e) => updateSlot(e.target.value)}>
              {marketSlots.map((slot) => <option key={slot.id} value={slot.time_slot}>{slot.time_slot}</option>)}
            </select>
          </div>
        </section>

        <section style={{ ...modeCard, ...(isMobile ? modeCardMobile : null), ...(isActiveBetScreen && isMobile ? activeModeCardMobile : null) }}>
          <div style={marketLine}>
            <span style={badge}>{initials(market?.name)}</span>
            <b>{market?.name || "STARLINE"}</b>
            <span style={slotChip}><Clock3 size={14} /> {timeSlot || "--"}</span>
          </div>
          {["single", "single_bulk"].includes(mode) && Number.isFinite(Number(ankStatus.ank_limit)) && Number(ankStatus.ank_limit) > 0 ? (
            <div style={ankInfoStrip}>
              <strong>{`Ank Limit: ${ankStatus.used_count}/${ankStatus.ank_limit}`}</strong>
              <small>
                {getSelectedNewDigitCount() >= Number(ankStatus.remaining || 0)
                  ? `Ank limit reached for ${timeSlot}. Other digits are locked.`
                  : `${Math.max(Number(ankStatus.remaining || 0) - getSelectedNewDigitCount(), 0)} more digit slots left for ${timeSlot}`}
              </small>
            </div>
          ) : null}
          {!mode ? (
            <div style={{ ...modeGrid, ...(isMobile ? modeGridMobile : null) }}>
              {modes.map((item) => {
                return (
                  <button
                    key={item.key}
                    type="button"
                    style={{ ...modeButton, ...(isMobile ? modeButtonMobile : null), borderBottomColor: item.color }}
                    onClick={() => {
                      resetInputs();
                      navigateWithSelection(timeSlot, item.key);
                    }}
                  >
                    <span style={{ ...modeIconWrap, background: item.tint, color: item.color }}>
                      {renderModeIcon(item)}
                    </span>
                    <span style={modeLabel}>{item.label}</span>
                    <ChevronRight size={16} color="#c7ced8" />
                  </button>
                );
              })}
            </div>
          ) : (
            <div style={{ ...selectedModeStrip, ...(isMobile ? selectedModeStripMobile : null), ...(isTight ? selectedModeStripTight : null) }}>
              <span style={activeModeIconWrap}>
                {renderModeIcon(activeMode)}
              </span>
              <span style={selectedModeText}>
                <small>Selected Game</small>
                <b>{activeMode.label}</b>
              </span>
              <button type="button" style={changeModeButton} onClick={() => { navigateWithSelection(timeSlot, ""); resetInputs(); }}>
                Back
              </button>
            </div>
          )}
        </section>

        {!mode ? (
          <section style={hintCard}>Select one Starline game card to continue.</section>
        ) : null}

        {mode === "single" && (
          <section style={{ ...singleFormCard, ...(isMobile ? singleFormCardMobile : null), ...(isTight ? singleFormCardTight : null) }}>
            <div style={{ ...digitGrid, ...(isMobile ? digitGridMobile : null), ...(isTight ? digitGridTight : null) }}>
              {digitAmounts.map((amount, digit) => (
                <div key={digit} style={{ ...digitBox, ...(isMobile ? digitBoxMobile : null) }}>
                  <b style={{ ...(isMobile ? digitLabelMobile : digitLabel), ...(Number(amount || 0) > 0 ? digitLabelActive : null), ...(isNewDigitLocked(digit) ? digitLabelDisabled : null) }}>{digit}</b>
                  <input
                    value={amount}
                    disabled={isNewDigitLocked(digit)}
                    onChange={(e) => updateSingleDigitAmount(digit, e.target.value)}
                    inputMode="numeric"
                    placeholder="₹"
                    style={{ ...amountInput, ...(isMobile ? amountInputMobile : null), ...(isNewDigitLocked(digit) ? disabledAmountInput : null) }}
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {mode === "single_bulk" && (
          <section style={{ ...formCard, ...(isMobile ? formCardMobile : null) }}>
            <label style={label}>Select Digits</label>
            <div style={{ ...digitGrid, ...(isMobile ? digitGridMobile : null) }}>
              {Array.from({ length: 10 }, (_, digit) => (
                <button
                  key={digit}
                  type="button"
                  style={{
                    ...(bulkDigits.includes(digit) ? selectedDigit : digitButton),
                    ...(isMobile ? digitButtonMobile : null),
                    ...(isNewDigitLocked(digit) ? disabledDigitButton : null),
                  }}
                  disabled={isNewDigitLocked(digit)}
                  onClick={() => toggleBulkDigit(digit)}
                >
                  {digit}{bulkDigits.includes(digit) && <Check size={14} />}
                </button>
              ))}
            </div>
            <input value={bulkAmount} onChange={(e) => setBulkAmount(e.target.value)} inputMode="numeric" placeholder="Amount per digit" style={{ ...wideInput, ...(isMobile ? wideInputMobile : null) }} />
          </section>
        )}

        {["sp", "dp", "tp"].includes(mode) && (
          <section style={{ ...formCard, ...(isMobile ? formCardMobile : null) }}>
            <label style={label}>Enter {activeMode.label} *</label>
            <input value={panna} onChange={(e) => setPanna(e.target.value.replace(/\D/g, "").slice(0, 3))} inputMode="numeric" placeholder="000" style={{ ...wideInput, ...(isMobile ? wideInputMobile : null) }} />
            <label style={label}>Amount *</label>
            <input value={pannaAmount} onChange={(e) => setPannaAmount(e.target.value)} inputMode="numeric" placeholder="Rs 0" style={{ ...wideInput, ...(isMobile ? wideInputMobile : null) }} />
          </section>
        )}
      </main>

      {mode ? (
        <footer style={{ ...footer, ...(isMobile ? footerMobile : null), ...(isTight ? footerTight : null) }}>
          <div><span>Total Amount</span><b>Rs {totalAmount}</b></div>
          <button type="button" style={{ ...placeButton, ...(isMobile ? placeButtonMobile : null) }} disabled={busy || totalAmount <= 0} onClick={submit}>
            <Plus size={20} /> Place Bid
          </button>
        </footer>
      ) : null}
    </div>
  );
}

const initials = (name = "") => name.split(" ").filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "SL";

const page = { minHeight: "100vh", background: "radial-gradient(circle at top, #fff7e6 0%, #f8fafc 42%, #eef2ff 100%)", paddingBottom: 92, fontFamily: "Inter, system-ui, sans-serif", color: "#111827" };
const pageMobile = { minHeight: "100dvh", paddingBottom: 76, overflowY: "auto", overflowX: "hidden", WebkitOverflowScrolling: "touch", boxSizing: "border-box" };
const pageTight = { paddingBottom: 66 };
const activePageMobile = { paddingBottom: 62 };
const header = { height: 58, background: "rgba(255,251,235,0.95)", borderBottom: "1px solid rgba(253,230,138,0.85)", display: "grid", gridTemplateColumns: "44px 1fr 44px", alignItems: "center", padding: "0 10px", position: "sticky", top: 0, zIndex: 10, backdropFilter: "blur(10px)", boxShadow: "0 8px 18px rgba(146,64,14,0.08)" };
const headerMobile = { height: 52, gridTemplateColumns: "40px 1fr 40px", padding: "0 8px" };
const activeHeaderMobile = { height: 48 };
const backButton = { width: 38, height: 38, borderRadius: "50%", border: 0, background: "#f8fafc", display: "grid", placeItems: "center", color: "#1f2937" };
const title = { margin: 0, justifySelf: "center", fontSize: "clamp(17px, 5vw, 22px)", fontWeight: 900, display: "flex", alignItems: "center", gap: 7 };
const headerSpacer = { width: 48 };
const main = { maxWidth: 760, width: "100%", margin: "0 auto", padding: "8px 8px 0", display: "grid", gap: 8, boxSizing: "border-box" };
const mainMobile = { padding: "6px 6px 0", gap: 7 };
const mainTight = { padding: "4px 5px 0", gap: 6 };
const activeMainMobile = { paddingBottom: 4, gap: 5 };
const selectorCard = { background: "linear-gradient(180deg,#fffaf0,#eff6ff)", border: "1px solid #bfdbfe", borderRadius: 18, padding: 10, display: "grid", gridTemplateColumns: "1fr", gap: 8, boxShadow: "0 10px 24px rgba(37,99,235,.08)" };
const selectorCardMobile = { padding: 8, borderRadius: 16, gap: 7 };
const selectorCardActiveMobile = { gridTemplateColumns: "1fr", padding: 6, gap: 6, borderRadius: 14 };
const selectorCardTight = { padding: 5, gap: 5 };
const label = { display: "block", color: "#1e3a8a", fontSize: 11, fontWeight: 900, marginBottom: 4 };
const select = { width: "100%", height: 46, border: "2px solid #bfdbfe", borderRadius: 13, background: "#fff", padding: "0 12px", fontWeight: 900, fontSize: 14 };
const selectMobile = { height: 38, borderRadius: 11, padding: "0 8px", fontSize: 12 };
const selectTight = { height: 34, fontSize: 11, padding: "0 6px" };
const modeCard = { background: "linear-gradient(180deg,#ffffff,#fff7ed)", border: "1px solid #fde68a", borderRadius: 16, padding: 10, display: "grid", gap: 10, boxShadow: "0 14px 30px rgba(180,83,9,.10)" };
const modeCardMobile = { padding: 7, gap: 7, borderRadius: 14 };
const activeModeCardMobile = { padding: 6, gap: 5, borderRadius: 12 };
const marketLine = { display: "flex", alignItems: "center", gap: 10, color: "#334155", flexWrap: "wrap" };
const badge = { width: 36, height: 36, background: "#2563eb", color: "#fff", display: "grid", placeItems: "center", fontWeight: 900, borderRadius: 10 };
const slotChip = { marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, color: "#64748b", fontWeight: 800 };
const ankInfoStrip = { minHeight: 42, borderRadius: 12, border: "1px solid #bfdbfe", background: "#eff6ff", color: "#1d4ed8", display: "grid", gap: 2, padding: "8px 10px", fontSize: 12 };
const modeGrid = { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 9 };
const modeGridMobile = { gridTemplateColumns: "1fr", gap: 7 };
const modeButton = { minHeight: 72, border: "1px solid #edf1f6", borderBottomWidth: "3px", borderBottomColor: "#dbe3ef", background: "#fff", color: "#334155", fontWeight: 900, borderRadius: 12, display: "grid", gridTemplateColumns: "38px minmax(0, 1fr) 14px", alignItems: "center", gap: 8, padding: "10px 8px", textAlign: "left", boxShadow: "0 8px 16px rgba(15,23,42,.045)" };
const modeButtonMobile = { minHeight: 62, padding: "9px 8px", gap: 7 };
const modeIconWrap = { width: 36, height: 36, borderRadius: 12, display: "grid", placeItems: "center", fontSize: 11, boxShadow: "inset 0 1px 0 rgba(255,255,255,.78)" };
const activeModeIconWrap = { ...modeIconWrap, background: "linear-gradient(180deg,#2563eb,#3b82f6)", color: "#fff", border: "1px solid rgba(255,255,255,.24)", boxShadow: "inset 0 1px 0 rgba(255,255,255,.26)" };
const modeIconText = { fontSize: 12, fontWeight: 900, letterSpacing: ".02em" };
const modeLabel = { fontSize: 12, lineHeight: 1.15, fontWeight: 800 };
const selectedModeStrip = { minHeight: 62, borderRadius: 14, border: "1px solid #dbeafe", background: "linear-gradient(135deg,#eff6ff,#fff)", display: "grid", gridTemplateColumns: "40px 1fr 74px", gap: 8, alignItems: "center", padding: "8px", boxShadow: "inset 0 1px 0 rgba(255,255,255,.84)" };
const selectedModeStripMobile = { minHeight: 56, gridTemplateColumns: "34px 1fr 56px", gap: 6, padding: "7px" };
const selectedModeStripTight = { minHeight: 48, gridTemplateColumns: "32px 1fr 58px", gap: 5, padding: 5, borderRadius: 11 };
const selectedModeText = { display: "grid", gap: 2, color: "#0f172a", fontSize: 12 };
const changeModeButton = { height: 38, border: 0, borderRadius: 12, background: "linear-gradient(135deg,#f59e0b,#ef4444)", color: "#fff", fontWeight: 900, fontSize: 12 };
const hintCard = { minHeight: 48, borderRadius: 14, background: "#fff", border: "1px dashed #cbd5e1", color: "#64748b", display: "grid", placeItems: "center", fontWeight: 800, fontSize: 13, padding: "0 10px", textAlign: "center" };
const formCard = { background: "linear-gradient(180deg,#fff,#fffaf0)", border: "1px solid #e2e8f0", borderRadius: 16, padding: 10, display: "grid", gap: 10, boxShadow: "0 10px 22px rgba(15,23,42,.06)" };
const formCardMobile = { padding: 7, gap: 7, borderRadius: 13 };
const singleFormCard = { ...formCard, background: "linear-gradient(180deg,#fff8e8,#fff4cf)", borderColor: "#facc15", boxShadow: "0 12px 26px rgba(180,83,9,.12), inset 0 1px 0 rgba(255,255,255,.95)" };
const singleFormCardMobile = { padding: 7, gap: 6, borderRadius: 13 };
const singleFormCardTight = { padding: 5, gap: 4, borderRadius: 11 };
const digitGrid = { display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 7, width: "100%" };
const digitGridMobile = { gap: 4, width: "100%" };
const digitGridTight = { gap: 3 };
const digitBox = { display: "grid", gap: 8, minWidth: 0 };
const digitBoxMobile = { gap: 5 };
const digitLabel = { height: 44, borderRadius: 12, border: "1px solid #facc15", background: "linear-gradient(180deg,#fff8dc,#fde9a8)", color: "#9a3412", display: "grid", placeItems: "center", fontSize: 15, fontWeight: 900 };
const digitLabelMobile = { ...digitLabel, height: 38, borderRadius: 10, fontSize: 13 };
const digitLabelActive = { background: "linear-gradient(135deg,#f59e0b,#ef4444)", color: "#fff", borderColor: "#f97316", boxShadow: "0 8px 16px rgba(249,115,22,.22)" };
const digitLabelDisabled = { background: "#f1f5f9", color: "#94a3b8", borderColor: "#e2e8f0" };
const amountInput = { height: 42, border: "2px solid #d1d5db", borderRadius: 11, textAlign: "center", fontWeight: 800, fontSize: 14, background: "#fff", boxShadow: "inset 0 2px 4px rgba(15,23,42,.08)", width: "100%", minWidth: 0 };
const amountInputMobile = { height: 34, borderRadius: 10, fontSize: 13 };
const disabledAmountInput = { background: "#f8fafc", color: "#94a3b8", borderColor: "#e2e8f0" };
const digitButton = { height: 46, border: "1px solid #bfdbfe", borderRadius: 12, background: "#dbeafe", color: "#1e3a8a", fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 };
const digitButtonMobile = { height: 34, borderRadius: 9, fontSize: 12 };
const disabledDigitButton = { background: "#f8fafc", color: "#94a3b8", borderColor: "#e2e8f0" };
const selectedDigit = { ...digitButton, background: "#2563eb", color: "#fff" };
const wideInput = { width: "100%", height: 48, border: "2px solid #d1d5db", borderRadius: 13, padding: "0 14px", boxSizing: "border-box", fontWeight: 800, fontSize: 15 };
const wideInputMobile = { height: 36, borderRadius: 10, padding: "0 10px", fontSize: 13 };
const footer = { position: "fixed", left: 0, right: 0, bottom: 0, background: "#fff", borderTop: "1px solid #e5e7eb", minHeight: 66, display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", padding: "0 10px", boxShadow: "0 -8px 24px rgba(15,23,42,.08)", boxSizing: "border-box", zIndex: 12 };
const footerMobile = { minHeight: 54, padding: "0 7px", gap: 7, fontSize: 12 };
const footerTight = { minHeight: 48, padding: "0 6px", fontSize: 11 };
const placeButton = { width: "min(100%, 240px)", height: 50, border: 0, borderRadius: 16, background: "linear-gradient(90deg,#60a5fa,#8b5cf6)", color: "#fff", fontSize: 16, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center", gap: 10 };
const placeButtonMobile = { width: "min(100%, 158px)", height: 40, borderRadius: 12, fontSize: 13, gap: 6 };

export default StarlinePlayPage;
